/**
 * Photo Metadata Service
 * Manages photo metadata operations via backend API
 * (Previously used DynamoDB directly, now uses API)
 */

import * as api from './api';
import { Photo, CreatePhotoInput, PhotoOrderUpdate } from '../types/photo';
import { getPhotoUrl, getThumbnailUrl } from './s3Service';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG photoMetadataService] Module loaded');

// API response wrapper type (backend wraps all responses)
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Get all photos sorted by order
 * API endpoint: GET /photos (public), GET /photos?includeHidden=true (admin - requires auth)
 * @param includeHidden - Include hidden photos (for admin view)
 */
export const getAllPhotos = async (includeHidden: boolean = false): Promise<Photo[]> => {
  debugLog('[DEBUG getAllPhotos] Fetching photos, includeHidden:', includeHidden);

  try {
    const endpoint = includeHidden ? '/photos?includeHidden=true' : '/photos';
    const response = await api.get<ApiResponse<Photo[]>>(endpoint);
    const photos = response.data || [];
    debugLog('[DEBUG getAllPhotos] Found', photos.length, 'photos');
    return photos;
  } catch (error) {
    console.error('[DEBUG getAllPhotos] Error fetching photos:', error);
    return [];
  }
};

/**
 * Get a photo by ID
 * API endpoint: GET /photos/{photoId}
 */
export const getPhotoById = async (photoId: string): Promise<Photo | null> => {
  debugLog('[DEBUG getPhotoById] Fetching photo:', photoId);

  try {
    const response = await api.get<ApiResponse<Photo>>(`/photos/${photoId}`);
    const photo = response.data;
    debugLog('[DEBUG getPhotoById] Retrieved photo:', photoId);
    return photo;
  } catch (error) {
    console.error('[DEBUG getPhotoById] Error fetching photo:', error);
    return null;
  }
};

/**
 * Create photo metadata after successful S3 upload
 * API endpoint: POST /photos
 * Backend handles ID generation and order assignment
 */
export const createPhotoMetadata = async (input: CreatePhotoInput): Promise<Photo> => {
  debugLog('[DEBUG createPhotoMetadata] Creating metadata for:', input.s3Key);

  try {
    const response = await api.post<ApiResponse<Photo>>('/photos', {
      s3Key: input.s3Key,
      filename: input.filename,
      caption: input.caption || '',
      contentType: input.contentType,
      fileSize: input.fileSize
    });

    const newPhoto = response.data;
    debugLog('[DEBUG createPhotoMetadata] Photo metadata created successfully:', newPhoto.photoId);
    return newPhoto;
  } catch (error) {
    console.error('[DEBUG createPhotoMetadata] Error creating photo metadata:', error);

    // Return a placeholder on error
    return {
      photoId: Date.now().toString(),
      s3Key: input.s3Key,
      filename: input.filename,
      caption: input.caption || '',
      order: 0,
      uploadedAt: new Date().toISOString(),
      contentType: input.contentType,
      fileSize: input.fileSize,
      thumbnailGenerated: false,
      visible: true
    };
  }
};

/**
 * Update photo metadata (requires authentication)
 * API endpoint: PUT /photos/{photoId}
 */
export const updatePhotoMetadata = async (
  photoId: string,
  updates: Partial<Omit<Photo, 'photoId'>>
): Promise<Photo | null> => {
  debugLog('[DEBUG updatePhotoMetadata] Updating photo:', photoId);

  try {
    const response = await api.put<ApiResponse<Photo>>(`/photos/${photoId}`, updates);
    const updatedPhoto = response.data;
    debugLog('[DEBUG updatePhotoMetadata] Photo updated successfully:', photoId);
    return updatedPhoto;
  } catch (error) {
    console.error('[DEBUG updatePhotoMetadata] Error updating photo:', error);
    return null;
  }
};

/**
 * Delete photo metadata (requires authentication)
 * API endpoint: DELETE /photos/{photoId}
 * Note: s3Service.deletePhotoFromS3 also calls this endpoint
 */
export const deletePhotoMetadata = async (photoId: string): Promise<boolean> => {
  debugLog('[DEBUG deletePhotoMetadata] Deleting photo:', photoId);

  try {
    await api.del(`/photos/${photoId}`);
    debugLog('[DEBUG deletePhotoMetadata] Photo deleted successfully:', photoId);
    return true;
  } catch (error) {
    console.error('[DEBUG deletePhotoMetadata] Error deleting photo:', error);
    return false;
  }
};

/**
 * Update photo order (for drag-and-drop reordering)
 * API endpoint: PUT /photos/{photoId}
 */
export const updatePhotoOrder = async (photoId: string, newOrder: number): Promise<boolean> => {
  debugLog('[DEBUG updatePhotoOrder] Updating order for:', photoId, 'to:', newOrder);

  const result = await updatePhotoMetadata(photoId, { order: newOrder });
  return result !== null;
};

/**
 * Batch update photo orders (for efficient reordering)
 * API endpoint: PATCH /photos/reorder (batch operation)
 */
export const batchUpdatePhotoOrders = async (updates: PhotoOrderUpdate[]): Promise<boolean> => {
  debugLog('[DEBUG batchUpdatePhotoOrders] Updating', updates.length, 'photo orders');

  try {
    await api.patch('/photos/reorder', { updates });
    debugLog('[DEBUG batchUpdatePhotoOrders] All orders updated successfully');
    return true;
  } catch (error) {
    console.error('[DEBUG batchUpdatePhotoOrders] Error updating orders:', error);
    return false;
  }
};

/**
 * Toggle photo visibility
 * @param photoId - The photo ID
 */
export const togglePhotoVisibility = async (photoId: string): Promise<Photo | null> => {
  debugLog('[DEBUG togglePhotoVisibility] Toggling visibility for:', photoId);

  const photo = await getPhotoById(photoId);
  if (!photo) {
    return null;
  }

  return updatePhotoMetadata(photoId, { visible: !photo.visible });
};

/**
 * Get photo count statistics
 */
export const getPhotoStats = async (): Promise<{ total: number; visible: number; hidden: number }> => {
  debugLog('[DEBUG getPhotoStats] Getting photo statistics');

  const allPhotos = await getAllPhotos(true);
  const visiblePhotos = allPhotos.filter(p => p.visible);

  return {
    total: allPhotos.length,
    visible: visiblePhotos.length,
    hidden: allPhotos.length - visiblePhotos.length
  };
};

/**
 * Reorder photos after drag-and-drop
 * @param photos - Array of photos in new order
 */
export const reorderPhotos = async (photos: Photo[]): Promise<boolean> => {
  debugLog('[DEBUG reorderPhotos] Reordering', photos.length, 'photos');

  const updates: PhotoOrderUpdate[] = photos.map((photo, index) => ({
    photoId: photo.photoId,
    order: index + 1
  }));

  return batchUpdatePhotoOrders(updates);
};

/**
 * Get photo with URLs computed
 * @param photo - The photo object
 */
export const getPhotoWithUrls = (photo: Photo): Photo & { url: string; thumbnailUrl: string } => {
  return {
    ...photo,
    url: getPhotoUrl(photo.s3Key),
    thumbnailUrl: getThumbnailUrl(photo.s3Key)
  };
};

/**
 * Get all photos with URLs computed
 * @param includeHidden - Include hidden photos
 */
export const getAllPhotosWithUrls = async (includeHidden: boolean = false): Promise<(Photo & { url: string; thumbnailUrl: string })[]> => {
  const photos = await getAllPhotos(includeHidden);
  return photos.map(getPhotoWithUrls);
};
